import React from 'react';
import { Spin } from 'antd';
import { PauseIcon } from '@/assets/icons/v2/PauseIcon';
import { PlayIcon } from '@/assets/icons/v2/PlayIcon';
import { useVideoPlayerContext } from './context';

import * as S from './styles';

const PLAYER_ICONS = {
	playing: PauseIcon,
	paused: PlayIcon
} as const;

export interface OverlayControlsProps {
	playIconSize?: number;
	isLoading?: boolean;
	spinnerSize?: 'default' | 'large';
	actionButton?: JSX.Element;
	actionLabel?: string;
	onActionClick?: () => void;
	onPlayClick?: () => void;
}

export function OverlayControls({
	actionButton,
	actionLabel,
	playIconSize = 48,
	onActionClick,
	onPlayClick,
	isLoading = false,
	spinnerSize = 'default'
}: Readonly<OverlayControlsProps>): JSX.Element {
	const { playingState, controlsVisible, handleTogglePlay } = useVideoPlayerContext();

	const IconComponent = playingState ? PLAYER_ICONS.playing : PLAYER_ICONS.paused;

	const showControls = !playingState || controlsVisible || isLoading;

	function handlePlayButtonClick(): void {
		if (isLoading) return;
		onPlayClick?.();
		handleTogglePlay();
	}

	return (
		<S.OverlayControlsLayer $show={showControls}>
			<S.PauseOverlay $visible={!playingState || isLoading} onClick={handlePlayButtonClick} aria-hidden="true" />

			<S.CenterPlayWrapper onClick={(e) => e.stopPropagation()}>
				{isLoading ? (
					<Spin size={spinnerSize} />
				) : (
					<S.ControlButton
						icon={
							<S.HoverIcon>
								<IconComponent width={playIconSize} height={playIconSize} />
							</S.HoverIcon>
						}
						onClick={handlePlayButtonClick}
						aria-label={playingState ? 'Pause' : 'Play'}
					/>
				)}
			</S.CenterPlayWrapper>

			{!isLoading && actionButton != null && onActionClick != null && (
				<S.TopLeftActionWrapper onClick={(e) => e.stopPropagation()}>
					<S.ControlButton
						icon={<S.HoverIcon>{actionButton}</S.HoverIcon>}
						onClick={onActionClick}
						aria-label={actionLabel}
					/>
				</S.TopLeftActionWrapper>
			)}
		</S.OverlayControlsLayer>
	);
}
